import { createAppAsyncThunk } from "@app/createAppAsyncThunk"
import { ConsultantClient } from "@clients/consultantClient"
import {
  ShiftDto,
  TeamLeadConsultants,
  UpdateConsultantProgramExpertiseRequestProgramDomainEnum,
  UpdateConsultantProgramExpertiseRequestProgramTypeEnum,
} from "@masterschool/course-builder-api"
import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import PendingDataState from "@utils/pendingDataState"
import { ShiftsClient } from "@clients/shiftsClient"
import { ConsultantAllocationsClient } from "@clients/consultantAllocationsClient"
import { showErrorSnackbar } from "@features/ui/uiSlice"

export type ConsultantToShifts = Record<string, ShiftDto[]>
export type SalesManagementSliceState = {
  salesTeams: PendingDataState<TeamLeadConsultants[]>
  shifts: PendingDataState<ConsultantToShifts>
  onlineConsultants: PendingDataState<string[]>
}

export const getInitialState: () => SalesManagementSliceState = () => {
  return {
    salesTeams: { status: "idle" },
    shifts: { status: "idle" },
    onlineConsultants: { status: "idle" },
  }
}

export const salesManagementSlice = createSlice({
  name: "salesManagementSlice",
  initialState: getInitialState(),
  reducers: {
    resetShifts: (state) => {
      state.shifts = { status: "idle" }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSalesTeams.pending, (state) => {
      // Keep the current teams on a refetch (e.g. after a team-lead
      // reassignment) so the whole table doesn't flash blank; only the
      // first load shows the pending state.
      if (state.salesTeams.status === "success") {
        return
      }
      state.salesTeams = { status: "pending" }
    })
    builder.addCase(
      fetchSalesTeams.fulfilled,
      (state, action: PayloadAction<TeamLeadConsultants[]>) => {
        state.salesTeams = {
          status: "success",
          data: action.payload,
        }
      },
    )
    builder.addCase(fetchSalesTeams.rejected, (state, action) => {
      state.salesTeams = {
        status: "error",
        error: action.error.message,
      }
    })
    builder.addCase(fetchShifts.pending, (state) => {
      if (state.shifts.status === "success") {
        return
      }
      state.shifts = { status: "pending" }
    })
    builder.addCase(fetchShifts.fulfilled, (state, action) => {
      if (action.payload !== "RESET_SHIFTS") {
        state.shifts = {
          status: "success",
          data: action.payload.reduce((acc, { consultantId, shifts }) => {
            acc[consultantId] = shifts
            return acc
          }, {} as ConsultantToShifts),
        }
      }
    })
    builder.addCase(updateConsultantAvailability.pending, (state, action) => {
      if (state.salesTeams.status !== "success" || !state.salesTeams.data) {
        return
      }
      const { consultantId, isAvailable } = action.meta.arg
      const allConsultants = state.salesTeams.data.flatMap(
        (team) => team.consultants,
      )
      const consultant = allConsultants.find(
        (consultant) => consultant.id === consultantId,
      )
      if (consultant) {
        consultant.isAvailable = isAvailable
      }
    })

    builder.addCase(updateConsultantAvailability.fulfilled, (state, action) => {
      if (state.salesTeams.status !== "success" || !state.salesTeams.data) {
        return
      }
      const consultantId = action.payload.id
      const allConsultants = state.salesTeams.data.flatMap(
        (team) => team.consultants,
      )
      const consultant = allConsultants.find(
        (consultant) => consultant.id === consultantId,
      )
      if (consultant) {
        Object.assign(consultant, action.payload)
      }
    })

    builder.addCase(updateConsultantTags.pending, (state, action) => {
      if (state.salesTeams.status !== "success" || !state.salesTeams.data) {
        return
      }
      const { consultantId, tags } = action.meta.arg
      const consultant = state.salesTeams.data
        .flatMap((team) => team.consultants)
        .find((consultant) => consultant.id === consultantId)
      if (consultant) {
        ;(consultant as unknown as { tags: string[] }).tags = tags
      }
    })

    builder.addCase(updateConsultantAvailability.rejected, (state, action) => {
      if (state.salesTeams.status !== "success" || !state.salesTeams.data) {
        return
      }
      const consultantId = action.meta.arg.consultantId
      const allConsultants = state.salesTeams.data.flatMap(
        (team) => team.consultants,
      )
      const consultant = allConsultants.find(
        (consultant) => consultant.id === consultantId,
      )
      if (consultant) {
        consultant.isAvailable = !consultant.isAvailable
      }
    })
    builder.addCase(editShift.pending, (state, action) => {
      const shifts = state.shifts
      if (shifts.status !== "success" || !shifts.data) {
        return
      }
      const keys = Object.keys(shifts.data)
      keys.forEach((key) => {
        const consultantShifts = shifts.data[key]
        consultantShifts.forEach((shift) => {
          if (shift.id === action.meta.arg.id) {
            Object.assign(shift, action.meta.arg)
          }
        })
      })
    })
    builder.addCase(editShift.fulfilled, (state, action) => {
      const shifts = state.shifts
      if (shifts.status !== "success" || !shifts.data) {
        return
      }
      const keys = Object.keys(shifts.data)
      keys.forEach((key) => {
        const consultantShifts = shifts.data[key]
        consultantShifts.forEach((shift) => {
          if (shift.id === action.payload.id) {
            Object.assign(shift, action.payload)
          }
        })
      })
    })
    builder.addCase(fetchConsultantOnlineStatus.pending, (state) => {
      if (state.onlineConsultants.status !== "success") {
        state.onlineConsultants = { status: "pending" }
      }
    })
    builder.addCase(fetchConsultantOnlineStatus.fulfilled, (state, action) => {
      state.onlineConsultants = {
        status: "success",
        data: action.payload,
      }
    })
    builder.addCase(
      updateConsultantProgramExpertise.fulfilled,
      (state, action) => {
        if (state.salesTeams.status !== "success" || !state.salesTeams.data) {
          return
        }
        const consultantId = action.payload.id
        const allConsultants = state.salesTeams.data.flatMap(
          (team) => team.consultants,
        )
        const consultant = allConsultants.find(
          (consultant) => consultant.id === consultantId,
        )
        if (consultant) {
          Object.assign(consultant, action.payload)
        }
      },
    )
  },
})

export const fetchSalesTeams = createAppAsyncThunk(
  "salesManageement/fetchConsultants",
  async () => {
    return ConsultantClient.getSalesTeams()
  },
)

export const fetchShifts = createAppAsyncThunk(
  "salesManageement/fetchShifts",
  async (_, thunkAPI) => {
    const salesTeams = thunkAPI.getState().salesManagement.salesTeams
    if (salesTeams.status !== "success" || !salesTeams.data) {
      thunkAPI.dispatch(salesManagementSlice.actions.resetShifts())
      return "RESET_SHIFTS"
    }
    const consultants = salesTeams.data.flatMap((team) => team.consultants)
    const shiftsMap = await ShiftsClient.getShiftsForConsultants(
      consultants.map((consultant) => consultant.id),
    )
    return consultants.map((consultant) => ({
      consultantId: consultant.id,
      shifts: shiftsMap[consultant.id],
    }))
  },
)

export const updateConsultantAvailability = createAppAsyncThunk(
  "salesManagement/updateConsultantAvailablity",
  async (params: { isAvailable: boolean; consultantId: string }) => {
    return ConsultantClient.updateConsultantAvailability(
      params.consultantId,
      params.isAvailable,
    )
  },
)

export const updateConsultantTags = createAppAsyncThunk(
  "salesManagement/updateConsultantTags",
  async (params: { tags: string[]; consultantId: string }, thunkAPI) => {
    return ConsultantClient.updateConsultantTags(
      params.consultantId,
      params.tags,
    ).catch(() => {
      thunkAPI.dispatch(fetchSalesTeams())
    })
  },
)

export const updateConsultantTeamLead = createAppAsyncThunk(
  "salesManagement/updateConsultantTeamLead",
  async (
    params: { consultantId: string; teamLeadUserId: string },
    thunkAPI,
  ) => {
    return ConsultantClient.updateConsultantTeamLead(
      params.consultantId,
      params.teamLeadUserId,
    )
      .catch((err) => {
        thunkAPI.dispatch(showErrorSnackbar(err.message))
      })
      .finally(() => {
        // Re-sync from the server: on success the consultant moves to its
        // new team's accordion; on failure the dropdown reverts to truth.
        thunkAPI.dispatch(fetchSalesTeams())
      })
  },
)

export const updateConsultants = createAppAsyncThunk(
  "salesManagement/updateConsultants",
  async (_, thunkApi) => {
    thunkApi.dispatch(fetchSalesTeams())
  },
)

export const editShift = createAppAsyncThunk(
  "salesManagement/editShift",
  async (shiftDto: ShiftDto, thunkApi) => {
    return ShiftsClient.editShift(shiftDto).finally(() => {
      thunkApi.dispatch(fetchShifts())
    })
  },
)

export const fetchConsultantOnlineStatus = createAppAsyncThunk(
  "salesManagement/fetchConsultantOnlineStatus",
  async (_, thunkApi) => {
    const state = thunkApi.getState()
    const salesTeams =
      state.salesManagement.salesTeams.status === "success"
        ? state.salesManagement.salesTeams.data
        : []
    const allConsultants = salesTeams.flatMap((team) => team.consultants)
    const consultantIds = allConsultants.map((consultant) => consultant.id)
    return ConsultantAllocationsClient.listOnlineConsultants(consultantIds)
  },
)

export const archiveConsultant = createAppAsyncThunk(
  "salesManagement/archiveConsultant",
  async (params: { consultantId: string }, thunkAPI) => {
    return ConsultantClient.archiveConsultant(params.consultantId)
      .then(() => thunkAPI.dispatch(fetchSalesTeams()))
      .catch(() =>
        thunkAPI.dispatch(showErrorSnackbar(`Failed to archive consultant`)),
      )
  },
)

export const updateConsultantProgramExpertise = createAppAsyncThunk(
  "salesManagement/updateConsultantProgramExpertise",
  async (params: {
    consultantId: string
    programType?: UpdateConsultantProgramExpertiseRequestProgramTypeEnum
    programDomain?: UpdateConsultantProgramExpertiseRequestProgramDomainEnum
  }) => {
    return ConsultantClient.updateConsultantProgramExpertise(
      params.consultantId,
      params.programType,
      params.programDomain,
    )
  },
)

export default salesManagementSlice.reducer
